import React, { useEffect, useState } from 'react';
import {
  PlayArrowRounded,
  LocalFireDepartmentRounded,
} from '@mui/icons-material';
import { TTS_VERSION } from '@src/constants/tts';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import route from '@src/constants/route';
import CustomTypography from '@src/new-components/Typography';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import actions from '@src/redux/actions';
import { MOE_PAYWALL_TYPE } from '@src/constants/moengage';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import { COLOR } from '@src/styles/color';
import {
  FEATURE,
  PACK_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_LEVEL_V3,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { checkVietNam } from '@src/utils/checkCountry';
import { checkFeaturePermission } from '@src/services/tts';
import CommonButton from '@src/new-components/Button';
import { useTranslation } from 'react-i18next';
import { BadgeIcon } from '@src/containers/VoiceLibrary/Content/VoiceItem/VoiceInfo';
import { VOICE_LEVEL, VOICE_PROVIDER } from '@src/constants/voice';
import {
  FAVOURITE_VOICES_ACTION,
  TRENDING_VOICE_CREDITS,
} from '@src/constants/voiceLibrary';
import AudioAvatar from '@src/new-components/Avatar/AudioAvatar';
import { Button, IconButton, Typography } from '@mui/material';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import useUserInfo from '@src/hooks/useTtsUser';
import { useCanUseEOLVoice } from '@src/hooks/useCanUseEOLVoice';
import CrownImg from '@src/assets/images/crown.png';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { SUB_STATUS } from '@src/constants/voiceCloning';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { convertFormatDate } from '@src/utils/date';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import { getFrameVoiceUrl } from '@src/services/voiceCloning';
import {
  isVoiceEligibleForTrial,
  isVoiceTrialLimitReached,
} from '@src/services/voiceTrial';
import {
  formatNameForEOL,
  getUpgradePackageLabelKey,
} from '@src/services/voice';
import { StyledVoice } from './index.styled';

const Voice = ({
  voice,
  onSelectedVoice,
  usingPackageType,
  onChangePlayAudio,
  active,
  isMyVoice,
  isFavourite,
  handleUpdateFavouriteVoices,
  canFavouriteVoices,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const { openSentences } = useSelector((state) => state.synthesisRequest);
  const { ttsUser } = useUserInfo();
  const {
    usingPackage,
    usingDubbingPackage,
    trialVoiceUsage: stateTrialVoiceUsage,
  } = useSelector((state) => state.user);
  const allPackages = useSelector((state) => state.packages.packages);
  const [isFavouriteLocal, setIsFavouriteLocal] = useState(isFavourite);
  const { defaultGlobalVoice } = useSelector((state) => state.voice);
  const { user } = useSelector((state) => state.auth);
  const { handleGetPackageCodeMapping } = useMappingPackage();
  const isVietNam = checkVietNam();
  const isDefaultGlobalVoice =
    !isVietNam &&
    usingPackage?.level === PACKAGE_LEVEL.BASIC &&
    voice?.code === defaultGlobalVoice;

  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();
  const userVoiceEolEnabled = getFeatureValue(FEATURE_KEYS.USER_VOICE_EOL, {
    userId: user.id,
  });
  const { canUseEOLVoice } = useCanUseEOLVoice();
  const canUseVoice = () => {
    if (!voice?.features?.length) return true;

    const usingPackageByType =
      usingPackageType === PACKAGE_TYPE.DUBBING ? ttsUser?.dubbing : ttsUser;

    if (!usingDubbingPackage && isDefaultGlobalVoice) return true;

    const eolVoice = voice?.eolDate;
    if (eolVoice && canUseEOLVoice({ voice, user: ttsUser })) return true;

    return voice.features.every((item) =>
      checkFeaturePermission(usingPackageByType?.features, item),
    );
  };

  const handleLimitedFeatureVoiceDialog = () => {
    const isBasicGlobalVoice = voice?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isAdvancedGlobalVoice = voice?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );

    if (isAdvancedGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            const upgradePackage = handleGetPackageCodeMapping({
              level: PACKAGE_LEVEL_V3.VIP,
              type: PACKAGE_TYPE.STUDIO,
              expiresIn: PACK_DURATION.YEAR,
            });
            history.push(`${route.PAYMENT}?package=${upgradePackage}`);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );

      return;
    }

    if (isBasicGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeSpecialOrVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(route.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t('useThisVoice') }),
      }),
    );
  };

  const handleChangePlayAudio = (e) => {
    e.stopPropagation();
    onChangePlayAudio(voice);
  };

  const handleCloseNotificationDialog = () =>
    dispatch(
      actions.dialog.displayDialog({
        notificationDialog: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const handleCloseNotificationDialogV2 = () =>
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: INIT_NOTIFICATION_DIALOG,
      }),
    );
  const voiceTrialConfig = getFeatureValue(FEATURE_KEYS.TRIAL_VOICE_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
    packageCode: user.packageCode,
  });
  const handleOpenVoiceTrialLimitDialog = (limitedVoice = voice) => {
    const packageName = t(
      getUpgradePackageLabelKey({
        voices: [limitedVoice],
        userFeatures: ttsUser?.features,
        packages: allPackages,
      }),
    );
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('trialVoiceLimitDescription', {
          voiceName: limitedVoice?.name || '',
          packageName,
        }),
        img: CrownImg,
        classNameImg: 'custom-img',
        maxWidth: 'sm',
        paywall: {
          type: MOE_PAYWALL_TYPE.VOICE_TRIAL_LIMIT,
          voiceCode: limitedVoice?.code,
        },
        onUpgrade: () => {
          history.push(route.PAYMENT);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );
  };

  const handleSelectedVoice = () => {
    if (voice?.version === TTS_VERSION.EMPHASIS && openSentences) {
      dispatch(
        actions.dialog.displayDialog({
          notificationDialog: {
            name: 'cannotUseVoice',
            title: t('cannotUseVoice'),
            description: t('cannotUseVoiceDes'),
            variant: 'warning',
            open: true,
            actionComponent: (
              <Button
                variant="contained"
                onClick={handleCloseNotificationDialog}
              >
                {t('understood')}
              </Button>
            ),
          },
        }),
      );
      return;
    }

    const canUse = canUseVoice();
    const isTrialEligible = isVoiceEligibleForTrial(voice, voiceTrialConfig);
    const isUsingTrial = isTrialEligible && !canUse;
    if (
      isUsingTrial &&
      isVoiceTrialLimitReached(voice, voiceTrialConfig, stateTrialVoiceUsage)
    ) {
      handleOpenVoiceTrialLimitDialog(voice);
      return;
    }

    if (canUse || isUsingTrial) {
      onSelectedVoice(voice);
    } else {
      handleLimitedFeatureVoiceDialog();
    }
  };

  const isAboutToBeRemoved = voice?.eolDate
    ? moment().isBefore(voice?.eolDate)
    : false;

  const isPublicVoiceCloning = voice?.status === SUB_STATUS.PUBLIC;

  const isVoiceCloningAboutToRemoved =
    isPublicVoiceCloning && voice?.discardAt
      ? moment().isBefore(voice?.discardAt)
      : false;

  const daysLeftBeforeVoiceInactive = moment(voice?.eolDate).diff(
    moment(),
    'days',
  );

  const voiceCloningRetentionDays = isPublicVoiceCloning
    ? voice?.retentionDays
    : null;

  const renderButtonUnderstand = () => (
    <CommonButton
      onClick={handleCloseNotificationDialogV2}
      variant="contained"
      customStyle={{
        borderRadius: '8px',
        height: '40px',
        width: '100%',
      }}
      textProps={{
        variantText: 'body-14-semibold',
        color: COLOR.indigo.default,
      }}
    >
      {t('gotIt')}
    </CommonButton>
  );

  const getRetentionDaysLabel = () => {
    if (isPublicVoiceCloning) {
      const publicVoiceCloningTagData = {};

      const daysLeftBeforeVoiceCloningDiscard =
        isPublicVoiceCloning && moment(voice?.discardAt).diff(moment(), 'days');

      if (isVoiceCloningAboutToRemoved) {
        publicVoiceCloningTagData.icon = (
          <RunningWithErrorsRoundedIcon
            sx={{ color: COLOR.redPrimary, fontSize: 16 }}
          />
        );
        publicVoiceCloningTagData.title = t(
          daysLeftBeforeVoiceCloningDiscard ? 'dayNumber' : 'today',
          {
            days: daysLeftBeforeVoiceCloningDiscard,
          },
        );
        publicVoiceCloningTagData.textColor = COLOR.redPrimary;
        publicVoiceCloningTagData.bgColor = COLOR.bgRedPrimary;
        publicVoiceCloningTagData.isVoiceAboutToBeRemoved = true;
        publicVoiceCloningTagData.contentTooltip = t(
          isMyVoice ? 'myVoiceCloningDiscardAt' : 'voiceCloningDiscardAt',
          {
            date: convertFormatDate(voice?.discardAt),
          },
        );

        return publicVoiceCloningTagData;
      }

      const keyRetentionDays =
        voiceCloningRetentionDays === 365 ? 'yearNumber' : 'dayNumber';

      publicVoiceCloningTagData.title = t(keyRetentionDays, {
        days: voiceCloningRetentionDays,
        value: 1,
      });
      publicVoiceCloningTagData.titlePopover = t('daysRetentionAfterRemove', {
        value: t(keyRetentionDays, {
          days: voiceCloningRetentionDays,
          value: 1,
        }),
      });
      publicVoiceCloningTagData.textColor = COLOR.descriptionUpload;
      publicVoiceCloningTagData.bgColor = COLOR.skyBlue;
      publicVoiceCloningTagData.contentTooltip = t(
        'voiceCloningRetentionDaysV2',
        {
          value: t(keyRetentionDays, {
            days: voiceCloningRetentionDays,
            value: 1,
          }),
        },
      );

      return publicVoiceCloningTagData;
    }

    const eolTagNameData = {
      textColor: COLOR.redPrimary,
      isVoiceAboutToBeRemoved: isAboutToBeRemoved,
      title: isAboutToBeRemoved
        ? t(daysLeftBeforeVoiceInactive ? 'dayNumber' : 'today', {
            days: daysLeftBeforeVoiceInactive,
          })
        : undefined,
      icon: (
        <RunningWithErrorsRoundedIcon
          sx={{ color: COLOR.redPrimary, fontSize: 16 }}
        />
      ),
      contentTooltip: t(
        userVoiceEolEnabled
          ? 'discontinuedAfterTimeV2'
          : 'discontinuedAfterTime',
        {
          date: moment(voice?.eolDate)?.format('DD/MM/YYYY'),
          name: formatNameForEOL(voice?.name, userVoiceEolEnabled),
        },
      ),
    };

    if (isAboutToBeRemoved) {
      return eolTagNameData;
    }
    return null;
  };

  const getGen2Label = () => {
    if (voice?.level === VOICE_LEVEL.GEN2)
      return (
        <>
          <div style={{ color: COLOR.descriptionUpload }}>•</div>
          <CustomTypography variant="body-14-regular" color={COLOR.blueDark}>
            {t('GEN2')}
          </CustomTypography>
        </>
      );

    return null;
  };

  const handleSelectTagVoiceCloningDiscardInfo = () => {
    const description = getRetentionDaysLabel().contentTooltip;

    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: {
          name: 'voice-cloning-discard-info',
          title: t('information'),
          description,
          open: true,
          actionComponent: renderButtonUnderstand(),
          showIcon: false,
          onClose: handleCloseNotificationDialogV2,
          variant: null,
        },
      }),
    );
  };

  const showTagsVoice = [
    voice?.category,
    VOICE_PROVIDER.VBEE_VOICE_CLONING !== voice?.provider &&
      voice?.language?.name?.[language],
  ]
    ?.filter(Boolean)
    ?.map((tag) => t(tag))
    ?.join(' • ');

  // handle update favourite voices

  useEffect(() => {
    setIsFavouriteLocal(isFavourite);
  }, [isFavourite]);

  return (
    <StyledVoice onClick={handleSelectedVoice}>
      <AudioAvatar
        avatar={voice?.avatar || voice?.image || voice?.roundImage}
        size={40}
        audioUrl={voice?.demo}
        audioIconColor={COLOR.yellow.default}
        badgeIcon={<BadgeIcon provider={voice?.provider} type={voice?.type} />}
        styleBadgeIcon={{ height: '16px !important', zIndex: 10 }}
        onPlayAudio={handleChangePlayAudio}
        onPauseAudio={handleChangePlayAudio}
        isPlaying={voice.playStatus}
        active={active}
        frameImageUrl={getFrameVoiceUrl(voice)}
      />
      <div className="content">
        <span className="voice-name">
          <CustomTypography
            variant="body-14-medium"
            color={COLOR.descriptionGray}
            style={{
              maxWidth:
                voice.stats?.totalCredits >= TRENDING_VOICE_CREDITS ||
                voice?.creditFactor > 1
                  ? 138
                  : 190,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
              verticalAlign: 'bottom',
            }}
          >
            {voice?.name}
          </CustomTypography>
          {voice?.creditFactor > 1 && (
            <span className="x2-char-tag">{`${t('charactersFactor', {
              factor: voice?.creditFactor,
            })}`}</span>
          )}
          {voice.stats?.totalCredits >= TRENDING_VOICE_CREDITS && (
            <div className="trending-label">
              <LocalFireDepartmentRounded
                sx={{
                  color: COLOR.functionalPink.default,
                  fontSize: 16,
                }}
              />
              <Typography variant="body-12-medium" color={COLOR.tagNew}>
                {t('featuredVoice')}
              </Typography>
            </div>
          )}
        </span>
        <div className="more-info-voice-box">
          <CustomTypography
            variant="body-14-regular"
            color={COLOR.descriptionUpload}
          >
            {showTagsVoice}
          </CustomTypography>
          {getGen2Label()}
          {getRetentionDaysLabel() && (
            <>
              <div style={{ color: COLOR.descriptionUpload }}>{' • '}</div>
              <CustomTypography
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectTagVoiceCloningDiscardInfo();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                variant="body-14-regular"
                color={getRetentionDaysLabel().textColor}
              >
                {getRetentionDaysLabel().icon}
                {getRetentionDaysLabel().title}
              </CustomTypography>
            </>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleUpdateFavouriteVoices({
              voiceCode: voice?.code,
              action: isFavouriteLocal
                ? FAVOURITE_VOICES_ACTION.REMOVE
                : FAVOURITE_VOICES_ACTION.ADD,
            });
            if (canFavouriteVoices && !isFavouriteLocal) {
              setIsFavouriteLocal(true);
              return;
            }
            setIsFavouriteLocal(false);
          }}
          aria-label="play"
          className="icon-button"
        >
          {isFavouriteLocal ? (
            <FavoriteRoundedIcon
              sx={{
                width: '20px',
                height: '20px',
                color: COLOR.indigo.default,
              }}
            />
          ) : (
            <FavoriteBorderRoundedIcon
              sx={{
                width: '20px',
                height: '20px',
                color: COLOR.indigo[80],
              }}
            />
          )}
        </IconButton>
        <IconButton
          onClick={handleChangePlayAudio}
          aria-label="play"
          className="icon-button"
        >
          {voice.playStatus ? (
            <PauseRoundedIcon
              sx={{
                fontSize: '24px',
                color: COLOR.indigo[80],
              }}
            />
          ) : (
            <PlayArrowRounded
              sx={{
                fontSize: '24px',
                color: COLOR.indigo[80],
              }}
            />
          )}
        </IconButton>
      </div>
    </StyledVoice>
  );
};

export default Voice;
